/**
 * Full-page boot-gate states rendered by `main.tsx`'s `AuthGate` (C1,
 * #22323) BEFORE the app shell/nav mounts — same reasoning as
 * `BOOT_PLACEHOLDER`: there is no nav chrome to embed these in yet, so both
 * render as a centered card on a bare page, not inside `ScreenHeader`/
 * `SubNav` the way a normal screen would. `GlassCard variant="strong"` per
 * `docs/design-system/CLAUDE.md`'s glass-tier guide (elevated/dialog-style
 * surfaces) — these ARE the whole page at this point, functionally a modal
 * gate in front of the app (was `variant="accent"`, the brand-accent tier,
 * before PR #22341 adversarial review finding #12).
 *
 * `NoPayrollAccessScreen` — `useOAuthEntityContext()` resolved `no_entity`
 * (the signed-in account's OAuth grant has no bound entity, e.g. an
 * identity-only consent). This is NOT a loading or error state — the sign-in
 * itself succeeded — so it renders as a plain informational `EmptyState`
 * with no RETRY action (a bound entity can only be granted from outside this
 * session, see #21675, the payroll entitlement dependency) but DOES offer a
 * sign-out action (PR #22341 adversarial review finding #4): without one,
 * a user signed into the wrong account had no recovery path at all — the
 * OAuth tokens stay valid, so a reload just shows this same screen again.
 * Never falls back to a mock session — see `use_session_facts.ts`'s module
 * docblock for why.
 *
 * `SessionErrorScreen` — `useSessionFacts()` resolved `error` (the real
 * `entityUser.me`/`entity.clientGet` fetch failed, even after
 * `resolveAuthBoot`'s own one-time grant-fetch retry succeeded at boot).
 * Offers a retry action, mirroring `MyPayScreen`'s `error` state. This is a
 * SESSION-FACTS-level error — it only happens after `AuthStatus` already
 * reached `authenticated` and a real entity context resolved.
 *
 * `GrantErrorScreen` — a DIFFERENT, earlier failure: `AuthStatus ===
 * 'grant_error'` (`lib/auth/auth_context.tsx`), meaning `resolveAuthBoot`
 * itself could not resolve the token's scoped firm/entity grant even after
 * its own retry, at AUTH-BOOT time — before identity/entity are even known.
 * Added in PR #22341 adversarial review findings #1/#2: this case used to
 * share the IdP-rejection `error` `AuthStatus`, which `AuthGate`'s guard
 * order made unreachable (rendered the blank `BOOT_PLACEHOLDER` forever
 * instead). Distinct copy from `SessionErrorScreen` on purpose — the two
 * failures happen at different points in the boot sequence and this one is
 * genuinely retry-worthy (see `AuthStatus`'s docblock).
 *
 * `LedgerSsoHandoffScreen` — the pre-auth landing surface for Ledger's Apps
 * launcher (issue #23943, `wagepro_login_screen.tsx`'s auto-SSO effect). Not
 * an error state either: an informational "Redirecting you to Ledger…" card
 * covering the brief window between clicking the launcher and `beginSignIn()`
 * navigating away to `/oauth/authorize`. No action buttons — a user who is
 * already signed in to Ledger sees this for a single frame or two on the way
 * to the consent screen; a user who is not signed in sees it on the way to
 * Ledger's own login page (which then returns them here to complete the
 * handoff). Reuses the same {@link BootGateShell}/`EmptyState` shell as every
 * other card in this file for visual continuity across the boot gates.
 *
 * `SignedOutScreen` — `AuthStatus === 'signed_out'`, i.e. the user pressed
 * "Sign out" in `AccountMenu`/`EmployeeTopBar`. Not an error state at all;
 * it exists so a sign-out has somewhere to LAND. See its own docblock for
 * why landing somewhere (instead of falling through to `unauthenticated`'s
 * automatic authorize redirect) is what makes sign-out actually observable.
 * It also carries a secondary "Create a company account" CTA to
 * `SIGNUP_PATH` (WagePro signup epic) — before this, a brand-new admin who
 * landed here (or on the automatic sign-in redirect's Ledger login page) had
 * nowhere to go to actually create an account; this screen was the one
 * reachable, unauthenticated surface that could offer that link. Its primary
 * "Sign In" action now navigates to the WagePro-branded `LOGIN_PATH` screen
 * (`wagepro_login_screen.tsx`) instead of re-invoking `beginSignIn()`
 * directly — that branded screen exists as of the same pass that added this
 * comment, so a returning user reaching this fallback card no longer bounces
 * through Ledger's generic, un-WagePro-branded `/login` page.
 */
import { Button, EmptyState, GlassCard } from '@jumpstone/ui-react';
import { useTranslation } from '@mukil/i18n';
import { SIGNUP_PATH } from '../../lib/auth/wagepro_signup_logic.js';
import { LOGIN_PATH } from '../../lib/auth/wagepro_login_logic.js';

/**
 * Full-page centred-card shell every boot-gate screen in this file shares.
 * Exported so `connect_landing_gate.tsx` / `first_run_setup_screen.tsx` (the
 * post-consent-landing surfaces, which sit at the same boot layer — no
 * `TopNav` / app chrome available yet) can use the exact same shell instead
 * of hand-repeating the `min-h-dvh flex … GlassCard variant="strong"…`
 * markup, which drifted between files during the pre-fixup review of
 * PR #23958.
 *
 * The `maxWidth` override is for the setup screen's wider single-panel
 * variant (720px vs. the default 480px used by every boot-gate card).
 */
export function BootGateShell({
  children,
  maxWidthClassName = 'max-w-[480px]',
  extraCardClassName,
}: {
  children: React.ReactNode;
  maxWidthClassName?: string;
  /**
   * Extra Tailwind classes appended to the inner card (e.g. `'space-y-6'`
   * for the setup screen's multi-section variant). Kept as an optional
   * escape hatch rather than baked into the default because most callers
   * render a single centred `EmptyState` and need no extra spacing.
   */
  extraCardClassName?: string;
}): React.JSX.Element {
  const cardClassName =
    `rounded-2xl p-6 w-full ${maxWidthClassName}` +
    (extraCardClassName !== undefined ? ` ${extraCardClassName}` : '');
  return (
    <div className="min-h-dvh flex items-center justify-center p-6">
      <GlassCard variant="strong" className={cardClassName}>
        {children}
      </GlassCard>
    </div>
  );
}

export function NoPayrollAccessScreen({
  signOut,
}: {
  /** `useAuth().signOut` from `lib/auth/auth_context.tsx` — the only recovery path from this screen. */
  signOut: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <BootGateShell>
      <EmptyState
        title={t('auth.no_payroll_access.title')}
        description={t('auth.no_payroll_access.description')}
        action={
          <Button variant="secondary" onClick={() => void signOut()}>
            {t('account.sign_out')}
          </Button>
        }
      />
    </BootGateShell>
  );
}

export function SessionErrorScreen({
  retry,
}: {
  retry: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <BootGateShell>
      <EmptyState
        title={t('auth.session_error.title')}
        description={t('auth.session_error.description')}
        action={
          <Button variant="secondary" onClick={retry}>
            {t('auth.session_error.retry')}
          </Button>
        }
      />
    </BootGateShell>
  );
}

/**
 * Auth-BOOT-level grant-fetch failure (`AuthStatus === 'grant_error'`) — see
 * this module's docblock for how this differs from `SessionErrorScreen`.
 */
export function GrantErrorScreen({
  retry,
}: {
  retry: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <BootGateShell>
      <EmptyState
        title={t('auth.grant_error.title')}
        description={t('auth.grant_error.description')}
        action={
          <Button variant="secondary" onClick={retry}>
            {t('auth.grant_error.retry')}
          </Button>
        }
      />
    </BootGateShell>
  );
}

/**
 * Landing card between "user clicked WagePro in Ledger's Apps launcher" and
 * the `beginSignIn()` navigation to `/oauth/authorize`. See this module's
 * docblock for the full rationale (issue #23943).
 */
export function LedgerSsoHandoffScreen(): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <BootGateShell>
      <EmptyState
        title={t('auth.ledger_sso_handoff.title')}
        description={t('auth.ledger_sso_handoff.description')}
      />
    </BootGateShell>
  );
}

/**
 * FALLBACK screen for a DELIBERATE sign-out (`AuthStatus === 'signed_out'`,
 * see `lib/auth/auth_context.tsx`) whose automatic redirect to Ledger's
 * sign-in page could not be started. It is no longer the normal landing
 * place: a sign-out now redirects straight to `/oauth/authorize` with OIDC
 * `prompt=login` (`REAUTHENTICATE_PROMPT`, `lib/auth/oauth_pkce.ts`), which
 * lands on Ledger's real sign-in form — so making the user read and
 * dismiss a card first was pure friction in front of the same destination.
 *
 * It exists at all because that redirect is the ONLY thing standing between a
 * signed-out user and a dead end: `signOut()` revokes this app's token pair
 * but cannot end the Ledger session cookie (host-scoped, shared with every
 * other first-party client), so there is no in-app state left to render. If
 * `beginSignIn()` itself rejects (no `crypto.subtle`, blocked
 * `sessionStorage`), this card and its button — which retries the identical
 * `prompt=login` request — are the recovery path.
 *
 * A bare redirect with NO prompt is what must never happen here: it hits the
 * auto-approve branch of `GET /oauth/authorize` and silently re-mints a
 * token, which is exactly what made the "Sign out" menu item look like a
 * no-op even once it was wired up.
 *
 * The `primary` Button (not `secondary` like the other boot gates) is
 * deliberate: unlike a retry-after-failure, signing back in is the one thing
 * a user is here to do, and it is a wanted action rather than a recovery
 * from an error.
 */
export function SignedOutScreen(): React.JSX.Element {
  const { t } = useTranslation('wp');
  return (
    <BootGateShell>
      <EmptyState
        title={t('auth.signed_out.title')}
        description={t('auth.signed_out.description')}
        action={
          <div className="flex flex-col items-center gap-2">
            <Button
              variant="primary"
              onClick={() => {
                window.location.href = LOGIN_PATH;
              }}
            >
              {t('auth.signed_out.sign_in')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                window.location.href = SIGNUP_PATH;
              }}
            >
              {t('auth.signed_out.create_account')}
            </Button>
          </div>
        }
      />
    </BootGateShell>
  );
}
